/**
 * Luxury Product Image Hover Zoom (Rachel Gilbert style)
 * - Instant 1st-hover activation (Preloaded 2800px high-res images on page load)
 * - Seamless instant switch from Image 1 to Image 2
 * - High-precision 60fps cursor tracking
 * - Mobile safe (disabled on touch screens < 750px)
 */

(function () {
  const ZOOM_LEVEL = 1.3; // 1.4x optimal magnification for sharp, clear details (0% blur)

  // Preload and setup high-res overlay for a single media item
  function setupSlideZoom(slide) {
    if (!slide) return;
    const img = slide.querySelector('img');
    if (!img) return;

    const mediaContainer = slide.querySelector('.product__media') || slide;

    // Get high-res zoom source
    let zoomSrc =
      img.getAttribute('data-zoom-src') ||
      mediaContainer.getAttribute('data-zoom-url') ||
      img.getAttribute('src');

    if (zoomSrc) {
      // Normalize width to 3840px for master sharpness (0% blur)
      zoomSrc = zoomSrc
        .replace(/([?&])width=\d+/, '$1width=3840')
        .replace(/_small|_medium|_compact|_large|_grande|_1024x1024|_2048x2048|_2800x/, '_3840x');
    }

    if (!zoomSrc) return;

    // Create or retrieve the luxury zoom overlay
    let overlay = mediaContainer.querySelector('.luxury-zoom-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'luxury-zoom-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      mediaContainer.style.position = 'relative';
      mediaContainer.style.overflow = 'hidden';
      mediaContainer.appendChild(overlay);
    }

    // Set background image and pre-cache immediately
    overlay.style.backgroundImage = `url("${zoomSrc}")`;
    overlay.style.backgroundSize = `${ZOOM_LEVEL * 100}%`;

    // Background pre-fetch
    const preloader = new Image();
    preloader.src = zoomSrc;

    return overlay;
  }

  // Setup all slides immediately
  function initAllSlides() {
    if (window.innerWidth < 750) return;
    const slides = document.querySelectorAll(
      '.product__media-list .product__media-item, media-gallery .product__media-item, .product__modal-opener--image'
    );
    slides.forEach((slide) => setupSlideZoom(slide));
  }

  // Global mouse move & hover coordinator (Event Delegation)
  let activeSlide = null;
  let activeOverlay = null;
  let animFrameId = null;
  let targetX = 50;
  let targetY = 50;
  let currentX = 50;
  let currentY = 50;

  function updateZoomPosition() {
    if (!activeOverlay) return;
    currentX += (targetX - currentX) * 0.35;
    currentY += (targetY - currentY) * 0.35;
    activeOverlay.style.backgroundPosition = `${currentX.toFixed(2)}% ${currentY.toFixed(2)}%`;
    animFrameId = requestAnimationFrame(updateZoomPosition);
  }

  function handlePointerOver(e) {
    if (window.innerWidth < 750) return;

    const slide = e.target.closest('.product__media-item, .product__modal-opener--image');
    if (!slide) return;

    // If moving to a new slide
    if (slide !== activeSlide) {
      if (activeOverlay) {
        activeOverlay.classList.remove('is-active');
      }
      activeSlide = slide;
      activeOverlay = setupSlideZoom(slide);

      if (activeOverlay) {
        const rect = slide.getBoundingClientRect();
        targetX = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
        targetY = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
        currentX = targetX;
        currentY = targetY;
        activeOverlay.style.backgroundPosition = `${currentX}% ${currentY}%`;
        activeOverlay.classList.add('is-active');

        if (animFrameId) cancelAnimationFrame(animFrameId);
        animFrameId = requestAnimationFrame(updateZoomPosition);
      }
    }
  }

  function handlePointerMove(e) {
    if (!activeSlide || !activeOverlay) return;
    const rect = activeSlide.getBoundingClientRect();
    targetX = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    targetY = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
  }

  function handlePointerOut(e) {
    if (!activeSlide) return;
    const related = e.relatedTarget;
    // Check if pointer has left the active slide completely
    if (!related || !activeSlide.contains(related)) {
      if (activeOverlay) {
        activeOverlay.classList.remove('is-active');
      }
      activeSlide = null;
      activeOverlay = null;
      if (animFrameId) {
        cancelAnimationFrame(animFrameId);
        animFrameId = null;
      }
    }
  }

  // Attach global delegation to media gallery
  function bindGalleryEvents() {
    const gallery = document.querySelector('media-gallery') || document.body;
    gallery.removeEventListener('pointerover', handlePointerOver);
    gallery.removeEventListener('pointermove', handlePointerMove);
    gallery.removeEventListener('pointerout', handlePointerOut);

    gallery.addEventListener('pointerover', handlePointerOver, { passive: true });
    gallery.addEventListener('pointermove', handlePointerMove, { passive: true });
    gallery.addEventListener('pointerout', handlePointerOut, { passive: true });
  }

  // Run on start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllSlides();
      bindGalleryEvents();
    });
  } else {
    initAllSlides();
    bindGalleryEvents();
  }

  // Re-run on variant changes, slide changes or DOM mutations
  document.addEventListener('variant:change', () => {
    setTimeout(() => {
      initAllSlides();
      bindGalleryEvents();
    }, 50);
  });

  const galleryEl = document.querySelector('media-gallery');
  if (galleryEl) {
    const observer = new MutationObserver(() => {
      initAllSlides();
      bindGalleryEvents();
    });
    observer.observe(galleryEl, { childList: true, subtree: true });
  }

  window.initProductZoom = () => {
    initAllSlides();
    bindGalleryEvents();
  };
})();
